import { FC, Fragment, MutableRefObject, ReactNode, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { isOk, isPending, Result } from "@/common/common";
import { Box, Card, Grid, VBox } from "@/components-v2/Box";
import { LocalizedText } from "@/common/common";
import { TMessageKey } from "@/i18n/types.gen";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { orderlessFilter } from "@/utils/filter";
import { Tabs } from "@/components-v2/Tabs";
import { MDateInput } from "@/components-v2/DateInput";
import { Skeleton } from "@/components-v2/Skeleton";
import { FieldValue, SystemFieldValue } from "@/customFields/customFieldsComponents";
import { CompanyCustomField, systemFieldLabels } from "@/customFields/customFieldsDomain";
import { companyRoleLabel } from "@/utils/Roles";
import { personFieldLabel, textFor } from "./dashboardDialogsDomain";
import { useDispatch, useLanguage, useTranslation } from "@/stores/RootStoreContext";
import { DashboardCardConfig, DashboardCardData } from "./dashboardApiClient";
import {
  cardButtons,
  cardSkeleton,
  type CardSkeletonShape,
  downloadGraphPng,
  loadPreviewData,
  MAP_CARD_HEIGHT,
  PreviewEntry,
  previewCompany,
  previewEntriesKey,
  previewGroup,
  previewNote,
  setPreviewCompany,
  setPreviewGroup,
  templateCardButtons,
} from "./dashboardPreviewDomain";
import { chosenDate, commitAsOfDate, DashboardSettingsState } from "./dashboardSettingsDomain";
import { Alert } from "@/components-v2/Alert";
import { accountUserGroupLabel } from "@/enterpriseAccounts/visibility/accountVisibilityDomain";
import { DataGraph } from "@/components-v2/DataGraph/DataGraph";
import { TemplateContent, TemplateType } from "@/templates/templateDomain";
import { GraphUtils } from "@/components-v2/DataGraph/dataGraphDomain";
import { LinkButton } from "@/components-v2/Button";
import { Dialog } from "@/components-v2/Dialog";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { mapTemplateGraphProps } from "@/templates/templatePanelDomain";

export const usePreviewData = (
  state: DashboardSettingsState,
  entries: PreviewEntry[],
  hasDatePicker: boolean,
  savedVisibleTo?: string[],
) => {
  const dispatch = useDispatch();
  const accountId = state.accountId;
  const orgNo = state.previewOrgNo;
  const asOf = chosenDate(hasDatePicker, state.previewAsOf);
  const previewAs = previewGroup(state, savedVisibleTo)?.grantee;
  const entriesKey = previewEntriesKey(entries);
  useEffect(() => {
    if (previewAs) {
      dispatch(loadPreviewData(accountId, orgNo, JSON.parse(entriesKey), { asOf, previewAs }));
    }
  }, [dispatch, accountId, orgNo, entriesKey, asOf, previewAs]);
};

export const PreviewAsSelect: FC<{ state: DashboardSettingsState; savedVisibleTo?: string[] }> =
  observer(({ state, savedVisibleTo }) => {
    const { t } = useTranslation();
    return (
      <Box gap={"sm"} alignItems={"center"} wrap>
        <Box fg={"subtle"} whitespace={"nowrap"}>
          {t("enterprise-account.dashboards.preview-as")}
        </Box>
        <PreviewGroupSelect state={state} savedVisibleTo={savedVisibleTo} />
        <PreviewCompanySelect state={state} />
      </Box>
    );
  });

const PreviewGroupSelect: FC<{ state: DashboardSettingsState; savedVisibleTo?: string[] }> =
  observer(({ state, savedVisibleTo }) => {
    const { tf } = useTranslation();
    const dispatch = useDispatch();
    const selected = previewGroup(state, savedVisibleTo);
    const groups = state.overview?.groups ?? [];
    return groups.length === 0 ? null : (
      <DropdownMenu
        button={{ type: "select", label: selected && tf(accountUserGroupLabel(selected)) }}
        alignment={"end"}
        items={groups.map((group) => ({
          type: "radio-item",
          label: accountUserGroupLabel(group),
          isSelected: group.grantee === selected?.grantee,
          onSelect: () => dispatch(setPreviewGroup(state, group.grantee)),
        }))}
      />
    );
  });

const PreviewCompanySelect: FC<{ state: DashboardSettingsState }> = observer(({ state }) => {
  const dispatch = useDispatch();
  const [filter, setFilter] = useState("");
  const selected = previewCompany(state);
  return state.companies.length === 0 ? null : (
    <DropdownMenu
      button={{ type: "select", label: selected?.name }}
      alignment={"end"}
      filter={filter}
      filterPlaceholder={{ key: "search" }}
      onFilterChange={setFilter}
      items={orderlessFilter(
        state.companies,
        (company) => [company.name, company.orgNo],
        filter,
      ).map((company) => ({
        type: "radio-item",
        label: company.name,
        isSelected: company.orgNo === state.previewOrgNo,
        onSelect: () => {
          setFilter("");
          dispatch(setPreviewCompany(state, company.orgNo));
        },
      }))}
    />
  );
});

export const PreviewVisibilityNote: FC<{
  state: DashboardSettingsState;
  savedVisibleTo?: string[];
  visibleTo: string[];
  isEnabled: boolean;
}> = observer(({ state, savedVisibleTo, visibleTo, isEnabled }) => {
  const message = previewNote(previewGroup(state, savedVisibleTo), visibleTo, isEnabled);
  return message ? <Alert type={"info"} message={message} /> : null;
});

export const PreviewTabBar: FC<{ title?: ReactNode; end?: ReactNode; inset?: boolean }> = ({
  title,
  end,
  inset,
}) => (
  <Box
    justifyContent={"space-between"}
    alignItems={"center"}
    gap={"md"}
    paddingX={inset ? "lg" : undefined}
    borderBottom
    wrap
  >
    {title ? (
      <Tabs tabs={[{ id: "preview", label: title, isActive: true, onSelect: () => undefined }]} />
    ) : (
      <span />
    )}
    {end && <Box paddingY={"sm"}>{end}</Box>}
  </Box>
);

export const PreviewHeadingBar: FC<{
  state: DashboardSettingsState;
  dateInputId: string;
  heading?: string;
  hasDatePicker: boolean;
}> = observer(({ state, dateInputId, heading, hasDatePicker }) => {
  const { t } = useTranslation();
  return heading || hasDatePicker ? (
    <Box justifyContent={"space-between"} alignItems={"center"} gap={"md"} wrap>
      {heading ? <h3>{heading}</h3> : <span />}
      {hasDatePicker && (
        <Box flex={"none"}>
          <MDateInput
            id={dateInputId}
            model={state.previewAsOf}
            placeholder={t("enterprise-account.dashboards.as-of-today")}
            onChange={(params) => commitAsOfDate(state.previewAsOf, params.input)}
          />
        </Box>
      )}
    </Box>
  ) : null;
});

const CardDataView: FC<{
  result?: Result<DashboardCardData>;
  config: DashboardCardConfig;
  graphRef?: MutableRefObject<GraphUtils | null>;
}> = observer(({ result, config, graphRef }) => {
  const { t } = useTranslation();
  if (!result) {
    return <Box fg={"subtle"}>{t("enterprise-account.dashboards.card-placeholder")}</Box>;
  }
  if (isPending(result)) {
    return <CardSkeleton shape={cardSkeleton(config)} />;
  }
  if (!isOk(result)) {
    return <Box fg={"error"}>{t("enterprise-account.dashboards.card-data-failed")}</Box>;
  }
  const data = result.data;
  switch (data.type) {
    case "ENTITY_DATA":
      return data.rows.length === 0 ? (
        <Box fg={"subtle"}>{t("enterprise-account.dashboards.no-fields")}</Box>
      ) : (
        <PropertyGrid
          rows={data.rows.map((row) =>
            row.source === "SYSTEM"
              ? {
                  key: row.field.systemField,
                  label: t(systemFieldLabels[row.field.systemField]),
                  value: <SystemFieldValue field={row.field} />,
                }
              : {
                  key: row.field.definitionId,
                  label: row.field.name,
                  value: <CustomValue field={row.field} />,
                },
          )}
        />
      );
    case "COMPANY_ROLES":
      return data.rows.length === 0 ? (
        <Box fg={"subtle"}>{t("enterprise-account.dashboards.no-holders")}</Box>
      ) : (
        <PropertyGrid
          rows={data.rows.map((row, index) => ({
            key: String(index),
            label: t(companyRoleLabel(row)),
            value: row.name,
          }))}
        />
      );
    case "ROLE_HOLDER":
      return data.holders.length === 0 ? (
        <Box fg={"subtle"}>{t("enterprise-account.dashboards.no-holders")}</Box>
      ) : (
        <VBox>
          {data.holders.map((holder, index) => (
            <PropertyGrid
              key={index}
              separated={index > 0}
              rows={holder.rows.map((row) =>
                row.source === "SYSTEM"
                  ? {
                      key: row.field,
                      label: t(personFieldLabel(row.field)),
                      value: <FieldValue value={row.value} />,
                    }
                  : {
                      key: row.field.definitionId,
                      label: row.field.name,
                      value: <CustomValue field={row.field} />,
                    },
              )}
            />
          ))}
        </VBox>
      );
    case "TEMPLATE":
      return data.content ? (
        <TemplateCardView content={data.content} graphRef={graphRef} />
      ) : (
        <Box fg={"error"}>{t("enterprise-account.dashboards.card-data-failed")}</Box>
      );
  }
});

const TemplateCardView: FC<{
  content: TemplateContent;
  graphRef?: MutableRefObject<GraphUtils | null>;
}> = ({ content, graphRef }) => {
  switch (content.type) {
    case TemplateType.PROJECT:
      return null;
    case TemplateType.MAP:
      return (
        <VBox grow overflow={"hidden"} htmlProps={{ style: { minHeight: MAP_CARD_HEIGHT } }}>
          <DataGraph {...mapTemplateGraphProps(content)} graphRef={graphRef} />
        </VBox>
      );
  }
};

export const DashboardCard: FC<{
  heading?: string;
  result?: Result<DashboardCardData>;
  config: DashboardCardConfig;
}> = ({ heading, result, config }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const name = heading || t("enterprise-account.dashboards.card-heading");
  const graphRef = useRef<GraphUtils | null>(null);
  const [isExpanded, setExpanded] = useState(false);
  const close = () => setExpanded(false);
  const buttons = [
    ...cardButtons(config, { EXPAND: () => setExpanded(true) }),
    ...templateCardButtons(config, {
      DOWNLOAD: () => downloadGraphPng(graphRef, name, dispatch),
    }),
  ];
  return (
    <>
      <Card
        fullheight
        title={
          <TextOrPlaceholder
            text={heading}
            placeholder={"enterprise-account.dashboards.card-heading"}
          />
        }
        action={
          buttons.length > 0 ? (
            <Box gap={"lg"}>
              {buttons.map((button, index) => (
                <LinkButton key={index} {...button} />
              ))}
            </Box>
          ) : undefined
        }
      >
        <CardDataView result={result} config={config} graphRef={graphRef} />
      </Card>
      {isExpanded && (
        <Dialog fullscreen onCancel={close}>
          <FullScreenLayout
            pageHeader={{ title: name, onGoBack: close, onGoBackVariant: "x" }}
            centerPanelContainerProps={{ overflow: "hidden" }}
            centerPanel={
              <VBox grow overflow={"hidden"} padding={"lg"}>
                <CardDataView result={result} config={config} />
              </VBox>
            }
          />
        </Dialog>
      )}
    </>
  );
};

const CardSkeleton: FC<{ shape: CardSkeletonShape }> = ({ shape }) => {
  switch (shape.type) {
    case "rows":
      return (
        <Grid cols={2} gap={"sm"}>
          {Array.from({ length: shape.count }, (_, index) => (
            <Fragment key={index}>
              <Skeleton width={"2/3"} />
              <Skeleton width={"1/2"} />
            </Fragment>
          ))}
        </Grid>
      );
    case "block":
      return (
        <Box
          className={"shimmer-block rounded-sm"}
          htmlProps={{ style: { height: shape.height } }}
        />
      );
  }
};

type PropertyRow = { key: string; label: string; value: ReactNode };

const PropertyGrid: FC<{ rows: PropertyRow[]; separated?: boolean }> = ({ rows, separated }) => (
  <Grid
    cols={2}
    gap={"sm"}
    borderTop={separated}
    paddingTop={separated ? "md" : undefined}
    marginTop={separated ? "md" : undefined}
  >
    {rows.map((row) => (
      <Fragment key={row.key}>
        <Box fg={"subtle"}>{row.label}</Box>
        <Box className={"min-w-0 break-words"}>{row.value}</Box>
      </Fragment>
    ))}
  </Grid>
);

const CustomValue: FC<{ field: CompanyCustomField }> = ({ field }) => (
  <FieldValue
    value={field.value}
    selectOptions={field.selectOptions}
    numberFormat={field.numberFormat}
  />
);

export const TextOrPlaceholder: FC<{ text?: string; placeholder: TMessageKey }> = ({
  text,
  placeholder,
}) => {
  const { t } = useTranslation();
  return text ? <>{text}</> : <Box fg={"subtle"}>{t(placeholder)}</Box>;
};

export const PreviewCard: FC<{
  state: DashboardSettingsState;
  heading: LocalizedText[];
  previewKey: string;
  config: DashboardCardConfig;
}> = observer(({ state, heading, previewKey, config }) => {
  const language = useLanguage();
  return (
    <DashboardCard
      heading={textFor(heading, language)}
      result={state.previewOrgNo ? state.previewData[previewKey] : undefined}
      config={config}
    />
  );
});
